import { CheckOutlined, ExclamationCircleOutlined, IconArrowRight } from '../../components/icons'
import { Card } from '../../components/ui/Card'
import type { Order } from './domain'
import { ORDER_STEPS, computeShippedFraction, resolveOrderStepState, stepState } from './domain'

export interface StatusStepperProps {
  readonly order: Pick<Order, 'status' | 'lines'>
}

/**
 * Order-level status stepper (UX spec §2.2 item 3, §2.3, §2.5, §2.9),
 * reconciled to the prototype's own "ORDER STATUS" card (`Fictiv
 * Platform.dc.html`, ~L3287-3298): a single horizontal row inside its own
 * card, each step a filled-circle checkmark + label, separated by a
 * long right-arrow glyph (`IconArrowRight`, UID-317 — not a connecting
 * bar/line, and not the plain `›` character this used to render).
 * Implemented as an ordered list
 * (`<ol>`) — not just colored dots — with a visually-hidden state word per
 * step ("completed"/"current"/"upcoming") and `aria-current="step"` on the
 * active step, so screen readers get the sequence and progress, not only a
 * visual fill.
 *
 * UID-317: the design (`Fictiv Platform.dc.html:10045`) shows a check glyph
 * in every step's dot, not just completed/current ones — upcoming steps get
 * a muted glyph (via the dot's own default text color) rather than an empty
 * circle. The mock's own data model is binary (done vs. not) — it doesn't
 * visually distinguish "current" from "completed" the way this codebase's
 * richer `resolveOrderStepState` does. Both `completed` and `current` get
 * the filled navy treatment here (matching the mock pixel-for-pixel);
 * `aria-current="step"` still marks the real current step for assistive
 * tech even though it looks the same as a completed one. The dot's glyph is
 * purely decorative (`aria-hidden`) — the `sr-only` state word carries the
 * meaning for assistive tech.
 *
 * `on_hold`/`cancelled` are rendered as an overlay banner ABOVE the stepper,
 * not a step of their own (§2.2 item 3 / §2.4) — the stepper underneath
 * keeps showing the underlying progress the interruption paused.
 * `partially_shipped` renders the Shipped step in a partial-fill state with
 * an accessible "N of M shipped" fraction in its name (§2.5 / §2.9), not
 * only a visual half-fill.
 */
export function StatusStepper({ order }: StatusStepperProps) {
  const { currentIndex, interrupted } = resolveOrderStepState(order)
  const fraction = computeShippedFraction(order)

  return (
    <div className="status-stepper-wrapper">
      {interrupted === 'on_hold' && (
        <output className="warning-banner">
          <ExclamationCircleOutlined aria-hidden="true" /> On hold — this order&rsquo;s progress is
          paused. Contact support for details.
        </output>
      )}
      {interrupted === 'cancelled' && (
        <output className="warning-banner warning-banner-muted">
          <ExclamationCircleOutlined aria-hidden="true" /> This order was cancelled.
        </output>
      )}
      <Card className="status-stepper-card">
        <ol className="status-stepper" aria-label="Order status">
          {ORDER_STEPS.map((step, index) => {
            const state = stepState(index, currentIndex)
            const isShippedPartial = step.id === 'shipped' && fraction !== null
            return (
              <li
                key={step.id}
                className={`status-step status-step-${state}`}
                aria-current={state === 'current' ? 'step' : undefined}
              >
                <span className="status-step-dot" aria-hidden="true">
                  <CheckOutlined aria-hidden="true" />
                </span>
                <span className="status-step-label">
                  {step.label}
                  {isShippedPartial && fraction && (
                    <span className="status-step-fraction">
                      {' '}
                      ({fraction.shipped} of {fraction.total} shipped)
                    </span>
                  )}
                </span>
                <span className="sr-only"> — {state}</span>
                {index < ORDER_STEPS.length - 1 && (
                  <span className="status-step-arrow" aria-hidden="true">
                    <IconArrowRight size={16} />
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </Card>
    </div>
  )
}

/** Neutral shimmer stepper for the route-level loading state (UX spec §2.6
 * Loading: "no stage highlighted until data resolves — avoid guessing a
 * stage during load"). */
export function StatusStepperSkeleton() {
  return (
    <Card className="status-stepper-card" aria-hidden="true">
      <ol className="status-stepper">
        {ORDER_STEPS.map((step, index) => (
          <li key={step.id} className="status-step status-step-upcoming">
            <span className="status-step-dot" />
            <span className="status-step-label">{step.label}</span>
            {index < ORDER_STEPS.length - 1 && (
              <span className="status-step-arrow" aria-hidden="true">
                <IconArrowRight size={16} />
              </span>
            )}
          </li>
        ))}
      </ol>
    </Card>
  )
}
